import { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'twilio-video';

export const useVideoCall = () => {
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [localAudioEnabled, setLocalAudioEnabled] = useState(true);
  const [localVideoEnabled, setLocalVideoEnabled] = useState(true);
  const roomRef = useRef(null);

  /**
   * Join a video room with the provided token
   */
  const joinRoom = useCallback(async (token, roomName, options = {}) => {
    if (!token || !roomName) {
      setError('Token and room name are required');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Default options for the room
      const connectOptions = {
        name: roomName,
        audio: options.audio !== false,
        video: options.video !== false ? { width: 640, height: 480 } : false,
        ...options
      };

      const connectedRoom = await connect(token, connectOptions);
      roomRef.current = connectedRoom;
      setRoom(connectedRoom);

      // Add existing participants
      const participantArray = Array.from(connectedRoom.participants.values());
      setParticipants(participantArray);

      // Handle new participants
      const handleParticipantConnected = (participant) => {
        setParticipants(prev => [...prev, participant]);
      };

      const handleParticipantDisconnected = (participant) => {
        setParticipants(prev => prev.filter(p => p.sid !== participant.sid));
      };

      connectedRoom.on('participantConnected', handleParticipantConnected);
      connectedRoom.on('participantDisconnected', handleParticipantDisconnected);

      // Handle reconnection events
      connectedRoom.on('reconnecting', (error) => {
        console.log('Reconnecting:', error);
      });

      connectedRoom.on('reconnected', () => {
        console.log('Reconnected to room');
      });

      setIsConnecting(false);
    } catch (err) {
      console.error('Failed to join room:', err);
      setError(`Failed to join room: ${err.message}`);
      setIsConnecting(false);
    }
  }, []);

  /**
   * Leave the current video room
   */
  const leaveRoom = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
      setRoom(null);
      setParticipants([]);
    }
  }, []);

  /**
   * Toggle local audio
   */
  const toggleAudio = useCallback(() => {
    if (room) {
      const enabled = !localAudioEnabled;
      room.localParticipant.audioTracks.forEach(publication => {
        if (enabled) {
          publication.track.enable();
        } else {
          publication.track.disable();
        }
      });
      setLocalAudioEnabled(enabled);
    }
  }, [room, localAudioEnabled]);

  /**
   * Toggle local video
   */
  const toggleVideo = useCallback(() => {
    if (room) {
      const enabled = !localVideoEnabled;
      room.localParticipant.videoTracks.forEach(publication => {
        if (enabled) {
          publication.track.enable();
        } else {
          publication.track.disable();
        }
      });
      setLocalVideoEnabled(enabled);
    }
  }, [room, localVideoEnabled]);

  /**
   * Cleanup when component unmounts
   */
  useEffect(() => {
    return () => {
      leaveRoom();
    };
  }, [leaveRoom]);

  return {
    room,
    participants,
    error,
    isConnecting,
    localAudioEnabled,
    localVideoEnabled,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo
  };
};
